// @warp-css;
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classNames as classnames } from "@chbphone55/classnames";
import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { reset } from "../styles.js";
import { uniqueId } from "../utils.js";
import { styles } from "./styles.js";
import { inputLabelStyles, inputHelpTextStyles } from "./input-styles.js";
// NOTE: Label and help-text are rendered inline using shared input styles.
// In a future major version, we could extract these into separate w-label and w-help-text components
// if we find significant reuse opportunities across non-input components.
const ccInput = {
    // input classes
    base: "block text-m leading-m mb-0 px-8 py-12 rounded-4 w-full focusable focus:[--w-outline-offset:-2px] caret-current", // true
    default: "border-1 s-text s-bg s-border-strong hover:s-border-strong-hover active:s-border-selected", // !isInvalid && !isDisabled && !isReadOnly
    disabled: "border-1 s-text-disabled s-bg-disabled-subtle s-border-disabled pointer-events-none", // !isInvalid && isDisabled && !isReadOnly
    invalid: "border-1 s-text-negative s-bg s-border-negative hover:s-border-negative-hover outline-[--w-s-color-border-negative]!", // isInvalid && !isDisabled && !isReadOnly
    readOnly: "pl-0 bg-transparent pointer-events-none", // !isInvalid && !isDisabled && isReadOnly
    placeholder: "placeholder:s-text-placeholder",
    suffix: "pr-40",
    prefix: "pl-[var(--w-prefix-width,_40px)]",
    // textarea classes
    textArea: "min-h-[42] sm:min-h-[45]",
    fixed: "resize-none",
};
/**
 * A multi-line text input with built-in form validation, auto-resizing, and styling support.
 *
 * The component automatically handles:
 *  - Form integration
 *  - Auto-resizing based on content and row constraints
 *  - Built-in validation with customizable error messages
 *  - Accessibility attributes and labeling
 */
class WarpTextarea extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * Keep in mind that using disabled in its current form is an anti-pattern.
         *
         * There will always be users who don't understand why an element is disabled, or users who can't even see that it is disabled because of poor lighting conditions or other reasons.
         *
         * Please consider more informative alternatives before choosing to use disabled on an element.
         *
         * @summary Makes the element not focusable and hides it from form submits
         */
        this.disabled = false;
        /**
         * Mark the form field as invalid.
         *
         * Make sure to also set a `help-text` to help users fix the validation problem.
         */
        this.invalid = false;
        /**
         * @deprecated Use the native `readonly` attribute instead
         */
        this.readOnly = false;
        /**
         * Whether the input can be selected but not changed by the user
         */
        this.readonly = false;
        /**
         * Whether user input is required on the input before form submission
         */
        this.required = false;
        /**
         * Show an icon behind the label indicating the field is optional
         */
        this.optional = false;
        /** @internal */
        this.minHeight = Number.NEGATIVE_INFINITY;
        /** @internal */
        this.maxHeight = Number.POSITIVE_INFINITY;
        // capture the initial value using connectedCallback and #initialValue
        this.#initialValue = undefined;
        // unique ID for this component instance
        this.#uniqueId = uniqueId("textarea-");
        // Track whether the current invalid/helpText state was set by validation
        this.#validationActive = false;
        // Store the original helpText to restore when validation passes
        this.#originalHelpText = undefined;
        // Track whether the user has interacted with the field
        this.#hasInteracted = false;
        /** @internal */
        this.#handleInvalid = (e) => {
            // Prevent browser's native validation bubble
            e.preventDefault();
            // Mark as interacted and show validation state
            this.#hasInteracted = true;
            this.#updateValidity();
        };
    }
    /** @internal */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    // capture the initial value using connectedCallback and #initialValue
    #initialValue;
    // unique ID for this component instance
    #uniqueId;
    // Track whether the current invalid/helpText state was set by validation
    #validationActive;
    // Store the original helpText to restore when validation passes
    #originalHelpText;
    // Track whether the user has interacted with the field
    #hasInteracted;
    updated(changedProperties) {
        if (changedProperties.has("value") && typeof this.value !== "undefined") {
            this.setValue(this.value);
        }
        if (changedProperties.has("value") ||
            changedProperties.has("required") ||
            changedProperties.has("disabled")) {
            this.#updateValidity();
        }
    }
    resetFormControl() {
        this.value = this.#initialValue;
        this.#hasInteracted = false;
        this.#clearValidationState();
        this.#updateValidity();
    }
    /** Returns the validation message if the textarea is invalid, otherwise an empty string */
    get validationMessage() {
        return this.internals.validationMessage;
    }
    /** Returns the validity state of the textarea */
    get validity() {
        return this.internals.validity;
    }
    /** Checks whether the textarea passes constraint validation */
    checkValidity() {
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** Checks validity and shows the browser's validation message if invalid */
    reportValidity() {
        this.#hasInteracted = true;
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** Sets a custom validation message. Pass an empty string to clear. */
    setCustomValidity(message) {
        if (message) {
            this.internals.setValidity({ customError: true }, message, this._textarea);
            this.#setValidationState(message);
        }
        else {
            this.#clearValidationState();
            this.#updateValidity();
        }
    }
    /** @internal */
    #setValidationState(message) {
        if (!this.#validationActive) {
            this.#originalHelpText = this.helpText;
        }
        this.#validationActive = true;
        this.invalid = true;
        this.helpText = message;
    }
    /** @internal */
    #clearValidationState() {
        if (this.#validationActive) {
            this.invalid = false;
            this.helpText = this.#originalHelpText;
            this.#originalHelpText = undefined;
            this.#validationActive = false;
        }
    }
    /** @internal */
    #updateValidity() {
        // Skip validation if disabled
        if (this.disabled) {
            this.internals.setValidity({});
            this.#clearValidationState();
            return;
        }
        // Check required validation
        if (this.required && !this.value) {
            // Get the browser's native validation message from the internal textarea
            const message = this._textarea?.validationMessage || "";
            this.internals.setValidity({ valueMissing: true }, message, this._textarea);
            // Only show visual validation state after user interaction
            if (this.#hasInteracted) {
                this.#setValidationState(message);
            }
            return;
        }
        // Valid state
        this.internals.setValidity({});
        this.#clearValidationState();
    }
    static { this.styles = [reset, styles, inputLabelStyles, inputHelpTextStyles]; }
    /** @internal */
    get _textareaStyles() {
        return classnames([
            ccInput.base,
            ccInput.textArea,
            !!this.placeholder && ccInput.placeholder,
            !this.invalid &&
                !this.disabled &&
                !(this.readonly || this.readOnly) &&
                ccInput.default,
            this.invalid &&
                !this.disabled &&
                !(this.readonly || this.readOnly) &&
                ccInput.invalid,
            !this.invalid &&
                this.disabled &&
                !(this.readonly || this.readOnly) &&
                ccInput.disabled,
            !this.invalid &&
                !this.disabled &&
                (this.readonly || this.readOnly) &&
                ccInput.readOnly,
            this.maxRows && ccInput.fixed,
        ]);
    }
    /** @internal */
    get _helptextstyles() {
        return "help-text";
    }
    /** @internal */
    get _helpId() {
        if (this.helpText)
            return `${this._id}__hint`;
        return undefined;
    }
    /** @internal */
    get _id() {
        return this.#uniqueId;
    }
    /** @internal */
    get _error() {
        if (this.invalid && this._helpId)
            return this._helpId;
        return undefined;
    }
    async connectedCallback() {
        super.connectedCallback();
        this.#initialValue = this.value;
        if (typeof this.value !== "undefined") {
            this.setValue(this.value);
        }
        // Listen for invalid event on the host element (fired by form validation)
        this.addEventListener("invalid", this.#handleInvalid);
        await this.updateComplete;
        if (this.value || this.minRows) {
            // If the component starts with a value or minHeight,
            // resize it automatically to show all the content, or maxHeight.
            const textarea = this.shadowRoot?.querySelector("textarea");
            if (textarea) {
                this.#resize(textarea);
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("invalid", this.#handleInvalid);
    }
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        // Initialize validity after the shadow DOM is ready
        this.#updateValidity();
    }
    handler(e) {
        const target = e.currentTarget;
        this.value = target.value;
        this.#resize(target);
    }
    /** @internal */
    #handleBlur() {
        this.#hasInteracted = true;
        this.#updateValidity();
    }
    /** @internal */
    #handleInvalid;
    /** Calculate the new height for the area on input */
    #resize(target) {
        const style = getComputedStyle(target);
        const borderTopWidth = Number.parseFloat(style.getPropertyValue("border-top-width"));
        const borderBottomWidth = Number.parseFloat(style.getPropertyValue("border-bottom-width"));
        const lineHeight = Number.parseFloat(style.getPropertyValue("line-height"));
        const topPadding = Number.parseFloat(style.getPropertyValue("padding-top"));
        const bottomPadding = Number.parseFloat(style.getPropertyValue("padding-bottom"));
        const offset = topPadding + bottomPadding + borderBottomWidth + borderTopWidth;
        if (this.minRows) {
            this.minHeight = lineHeight * this.minRows + offset;
        }
        if (this.maxRows) {
            this.maxHeight = lineHeight * this.maxRows + offset;
        }
        const borderBoxHeight = target.scrollHeight + borderTopWidth + borderBottomWidth;
        const height = Math.min(this.maxHeight, Math.max(this.minHeight, borderBoxHeight));
        target.style.setProperty("height", height + "px");
    }
    render() {
        return html `
			${this.label
            ? html `
						<label for="${this._id}">
							${this.label}
							${this.optional
                ? html `
										<span>
											${i18n._({
                    id: "textarea.label.optional",
                    message: "(optional)",
                    comment: "Shown behind label when marked as optional",
                })}
										</span>
									`
                : nothing}
						</label>
					`
            : nothing}
			<textarea
				part="input"
				id="${this._id}"
				class="${this._textareaStyles}"
				name="${ifDefined(this.name)}"
				placeholder="${ifDefined(this.placeholder)}"
				.value="${this.value || ""}"
				aria-describedby="${ifDefined(this._helpId ||
            (this.ariaDescription ? "aria-description" : undefined))}"
				aria-errormessage="${ifDefined(this._error)}"
				aria-invalid=${this.invalid ? "true" : nothing}
				?disabled="${this.disabled}"
				?readonly="${this.readonly || this.readOnly}"
				?required="${this.required}"
				@input="${this.handler}"
				@blur="${this.#handleBlur}"
			>
			</textarea>
			${this.helpText
            ? html `<div class="${this._helptextstyles}" id="${this._helpId}">
						${this.helpText}
					</div>`
            : nothing}
		`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "invalid", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "label", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: "help-text" })
], WarpTextarea.prototype, "helpText", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "maximum-rows" })
], WarpTextarea.prototype, "maxRows", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "minimum-rows" })
], WarpTextarea.prototype, "minRows", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "read-only" })
], WarpTextarea.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "optional", void 0);
__decorate([
    state()
], WarpTextarea.prototype, "minHeight", void 0);
__decorate([
    state()
], WarpTextarea.prototype, "maxHeight", void 0);
__decorate([
    query("textarea")
], WarpTextarea.prototype, "_textarea", void 0);
if (!customElements.get("w-textarea")) {
    customElements.define("w-textarea", WarpTextarea);
}
export { WarpTextarea };
//# sourceMappingURL=textarea.js.map